import { useMemo, useRef, useEffect } from "react";

export default function PasspartoutSegment({
  totalWidth,
  totalHeight,
  printWidth,
  printHeight,
  color,
  bevelColor,
  borderDepth,
  borderThickness,
  borderSlit,
  offsetX,
  offsetY,
  rotation,
}) {
  const faceRef = useRef();
  const bevelRef = useRef();

  const face = useMemo(() => {
    return createFaceGeometry(
      totalWidth,
      totalHeight,
      printWidth + borderSlit * 2,
      printHeight + borderSlit * 2,
      borderThickness,
      offsetX,
      offsetY
    );
  }, [
    totalWidth,
    totalHeight,
    printWidth,
    printHeight,
    borderThickness,
    borderSlit,
    offsetX,
    offsetY,
  ]);

  const bevel = useMemo(() => {
    return createBevelGeometry(
      totalWidth,
      totalHeight,
      printWidth + borderSlit * 2,
      printHeight + borderSlit * 2,
      borderThickness,
      offsetX,
      offsetY
    );
  }, [
    totalWidth,
    totalHeight,
    printWidth,
    printHeight,
    borderThickness,
    borderSlit,
    offsetX,
    offsetY,
  ]);

  const faceUV = useMemo(() => {
    return createFaceUV(
      totalWidth,
      totalHeight,
      printWidth + borderSlit * 2,
      printHeight + borderSlit * 2,
      borderThickness,
      offsetX,
      offsetY
    );
  }, [
    totalWidth,
    totalHeight,
    printWidth,
    printHeight,
    borderThickness,
    borderSlit,
    offsetX,
    offsetY,
  ]);

  const bevelUV = useMemo(() => {
    return createBevelUV(
      totalWidth,
      totalHeight,
      printWidth + borderSlit * 2,
      printHeight + borderSlit * 2,
      borderThickness,
      offsetX,
      offsetY
    );
  }, [
    totalWidth,
    totalHeight,
    printWidth,
    printHeight,
    borderThickness,
    borderSlit,
    offsetX,
    offsetY,
  ]);

  useEffect(() => {
    if (faceRef.current) {
      faceRef.current.computeVertexNormals();
      faceRef.current.attributes.position.needsUpdate = true;
      faceRef.current.attributes.uv.needsUpdate = true;
    }
  }, [totalWidth, totalHeight, borderThickness, borderSlit, offsetX, offsetY]);

  useEffect(() => {
    if (bevelRef.current) {
      bevelRef.current.computeVertexNormals();
      bevelRef.current.attributes.position.needsUpdate = true;
      bevelRef.current.attributes.uv.needsUpdate = true;
    }
  }, [totalWidth, totalHeight, borderThickness, borderSlit, offsetX, offsetY]);

  return (
    <>
      <mesh
        material={color}
        receiveShadow
        rotation-z={rotation}
        position-z={borderDepth}
      >
        <bufferGeometry ref={faceRef}>
          <bufferAttribute
            attach="attributes-position"
            count={6}
            itemSize={3}
            array={face}
          />
          <bufferAttribute
            attach="attributes-uv"
            count={6}
            itemSize={2}
            array={faceUV}
          />
        </bufferGeometry>
      </mesh>

      <mesh
        material={bevelColor}
        receiveShadow
        rotation-z={rotation}
        position-z={borderDepth}
      >
        <bufferGeometry ref={bevelRef}>
          <bufferAttribute
            attach="attributes-position"
            count={6}
            itemSize={3}
            array={bevel}
          />
          <bufferAttribute
            attach="attributes-uv"
            count={6}
            itemSize={2}
            array={bevelUV}
          />
        </bufferGeometry>
      </mesh>
    </>
  );
}

// The flat front face of the mat, entirely at full thickness.
function createFaceGeometry(tW, tH, pW, pH, th, oX, oY) {
  const vertices = new Float32Array([
    -pW / 2 - th + oX,      pH / 2 + th + oY,   th, // 0
    pW / 2 + th + oX,       pH / 2 + th + oY,   th, // 1
    tW / 2,                 tH / 2,             th, // 2

    -pW / 2 - th + oX,      pH / 2 + th + oY,   th, // 3
    tW / 2,                 tH / 2,             th, // 4
    -tW / 2,                tH / 2,             th, // 5
  ]);

  return vertices;
}

// The 45-degree insert: steps inward by `th` while dropping from z = th to z = 0,
// so run and rise are equal. Rendered with its own darker material.
function createBevelGeometry(tW, tH, pW, pH, th, oX, oY) {
  const vertices = new Float32Array([
    -pW / 2 - th + oX,      pH / 2 + th + oY,   th, // 12
    pW / 2 + th + oX,       pH / 2 + th + oY,   th, // 13
    pW / 2 + oX,            pH / 2 + oY,        0, // 14

    -pW / 2 - th + oX,      pH / 2 + th + oY,   th, // 15
    pW / 2 + oX,            pH / 2 + oY,        0, // 16
    -pW / 2 + oX,           pH / 2 + oY,        0, // 17
  ]);

  return vertices;
}

function createFaceUV(tW, tH, pW, pH, th, oX, oY) {
  const uv = new Float32Array([
    -pW / 2 - th + oX,      pH / 2 + th + oY, // 0
    pW / 2 + th + oX,       pH / 2 + th + oY, // 1
    tW / 2,                 tH / 2, // 2

    -pW / 2 - th + oX,      pH / 2 + th + oY, // 3
    tW / 2,                 tH / 2, // 4
    -tH / 2,                tH / 2, // 5
  ]);

  return uv;
}

function createBevelUV(tW, tH, pW, pH, th, oX, oY) {
  const uv = new Float32Array([
    -pW / 2,                pH / 2, // 0
    pW / 2,                 pH / 2, // 1
    tW / 2,                 tH / 2, // 2

    -pW / 2,                pH / 2, // 3
    tW / 2,                 tH / 2, // 4
    -tH / 2,                tH / 2, // 5
  ]);

  return uv;
}
